import { EntityAuditService } from 'app/entities/audit/entity-audit.service';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';

import { AuditFilter } from 'app/entities/audit/audit-filter.model';
import { EntityIdDetails } from './../../../shared/models/entity-id-details.model';
import { AuditTimelineEvent, AuditTimelineGroup } from './../audit.model';
import { formatDisplayDate } from 'app/shared/utils/date-format.util';

@Component({
    selector: 'audit-details',
    templateUrl: './audit-details.component.html',
    styleUrls: ['./audit-details.component.css'],
})

export class AuditDetailsComponent implements OnInit, OnChanges {
    @Input()
    entityIdDetails: EntityIdDetails;
    @Input()
    filters: AuditFilter;

    loadedEvents: AuditTimelineEvent[] = [];
    timelineGroups: AuditTimelineGroup[] = [];
    isAuditLoaded = false;
    isLoadingMore = false;
    pageSize = 50;
    currentOffset = 0;
    totalMatchingCount = 0;
    hasMoreToLoad = false;
    isFilteredEmpty = false;

    private loadRequestId = 0;

    constructor(private entityAuditService: EntityAuditService) {
    }

    ngOnInit() {
        this.entityAuditService.updateAuditInformation.subscribe(event => {
            if (event === 'Add') {
                this.reloadAuditDetails(true);
            }
        });
        this.tryInitialLoad();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['filters'] || changes['entityIdDetails']) {
            this.tryInitialLoad();
        }
    }

    private tryInitialLoad() {
        if (this.canLoad()) {
            this.reloadAuditDetails(true);
        }
    }

    private canLoad(): boolean {
        return !!(this.entityIdDetails && this.entityIdDetails.entityId);
    }

    reloadAuditDetails(resetPagination: boolean) {
        if (!this.canLoad()) {
            return;
        }
        if (resetPagination) {
            this.currentOffset = 0;
            this.loadedEvents = [];
            this.isAuditLoaded = false;
        }
        this.fetchTimelinePage(resetPagination);
    }

    private fetchTimelinePage(replaceExisting: boolean) {
        const requestId = ++this.loadRequestId;
        if (replaceExisting) {
            this.isAuditLoaded = false;
        }

        this.entityAuditService.getAuditTimeline(
            this.entityIdDetails.entityId,
            this.filters || new AuditFilter(),
            this.currentOffset,
            this.pageSize
        ).subscribe(
            response => {
                if (requestId !== this.loadRequestId) {
                    return;
                }
                this.applyTimelineResponse(response, replaceExisting);
                this.finishLoading(requestId);
            },
            () => {
                if (requestId !== this.loadRequestId) {
                    return;
                }
                if (replaceExisting) {
                    this.loadedEvents = [];
                    this.timelineGroups = [];
                    this.totalMatchingCount = 0;
                }
                this.finishLoading(requestId);
            }
        );
    }

    private applyTimelineResponse(response: any, replaceExisting: boolean) {
        const parsed = this.parseTimelineResponse(response);
        if (replaceExisting) {
            this.loadedEvents = parsed.events;
        } else {
            this.loadedEvents = this.loadedEvents.concat(parsed.events);
        }
        this.totalMatchingCount = parsed.total;
        this.hasMoreToLoad = this.loadedEvents.length < this.totalMatchingCount;
        this.isFilteredEmpty = this.totalMatchingCount === 0 && this.hasActiveFilters();
        this.timelineGroups = this.groupByDate(this.loadedEvents);
    }

    private finishLoading(requestId: number) {
        if (requestId === this.loadRequestId) {
            this.isAuditLoaded = true;
            this.isLoadingMore = false;
        }
    }

    private parseTimelineResponse(response: any): { events: AuditTimelineEvent[], total: number } {
        const result = response && response.arrResult;
        if (!result) {
            return { events: [], total: 0 };
        }

        if (Array.isArray(result.events)) {
            return {
                events: this.mapTimelineEvents(result.events),
                total: typeof result.total === 'number' ? result.total : result.events.length
            };
        }

        // Fallback for legacy journal/tasks response shape.
        if (result.journalEntries || result.finishedTasks) {
            const events = this.mapLegacyResponse(result);
            return { events: events, total: events.length };
        }

        return { events: [], total: 0 };
    }

    private mapLegacyResponse(result: any): AuditTimelineEvent[] {
        const events: AuditTimelineEvent[] = [];
        (result.finishedTasks || []).forEach(task => {
            events.push(this.mapLegacyTask(task));
        });
        (result.journalEntries || []).forEach(journalEntry => {
            events.push(this.mapLegacyJournalEntry(journalEntry));
        });
        return events.sort((a, b) => this.getEntryTime(b) - this.getEntryTime(a));
    }

    private mapLegacyTask(task: any): AuditTimelineEvent {
        const event = new AuditTimelineEvent();
        event.id = 'task-' + task.id;
        event.sourceType = 'TASK';
        event.eventCategory = 'TASK';
        event.title = task.title || 'Task completed';
        event.description = task.description;
        event.author = task.assignee || task.owner;
        event.workflowName = task.workflowName;
        event.submitted = !!task.isSubmitted;
        event.icon = 'assignment_turned_in';
        event.timestamp = task.endDate ? new Date(task.endDate) : null;
        event.comments = task.comments || [];
        return event;
    }

    private mapLegacyJournalEntry(journalEntry: any): AuditTimelineEvent {
        const event = new AuditTimelineEvent();
        event.id = 'journal-' + journalEntry.id;
        event.sourceType = 'JOURNAL';
        event.eventCategory = journalEntry.entityType || 'MANUAL';
        event.actionType = journalEntry.actionType;
        event.entityType = journalEntry.entityType;
        event.title = journalEntry.title || 'Information change';
        event.description = journalEntry.text;
        event.author = journalEntry.author;
        event.icon = 'track_changes';
        event.timestamp = journalEntry.entryDate ? new Date(journalEntry.entryDate) : null;
        event.comments = [];
        return event;
    }

    private mapTimelineEvents(events: any[]): AuditTimelineEvent[] {
        if (!Array.isArray(events)) {
            return [];
        }
        return events.map(event => {
            const mapped = new AuditTimelineEvent();
            mapped.id = event.id;
            mapped.sourceType = event.sourceType;
            mapped.eventCategory = event.eventCategory;
            mapped.actionType = event.actionType;
            mapped.entityType = event.entityType;
            mapped.timestamp = event.timestamp ? new Date(event.timestamp) : null;
            mapped.title = event.title;
            mapped.description = event.description;
            mapped.author = event.author;
            mapped.workflowName = event.workflowName;
            mapped.submitted = !!event.submitted;
            mapped.icon = event.icon || 'track_changes';
            mapped.comments = event.comments || [];
            return mapped;
        });
    }

    private getEntryTime(entry: AuditTimelineEvent): number {
        if (!entry || !entry.timestamp) {
            return 0;
        }
        return new Date(entry.timestamp).getTime();
    }

    private hasActiveFilters(): boolean {
        const activeFilters = this.filters || new AuditFilter();
        return !!(activeFilters.searchText || activeFilters.userId || activeFilters.workflowType
            || activeFilters.startDate || activeFilters.endDate
            || (activeFilters.eventTypes && activeFilters.eventTypes.length));
    }

    private groupByDate(entries: AuditTimelineEvent[]): AuditTimelineGroup[] {
        const groups: AuditTimelineGroup[] = [];
        let currentLabel: string = null;
        let currentGroup: AuditTimelineGroup = null;

        entries.forEach(entry => {
            const label = this.getDateGroupLabel(entry.timestamp);
            if (label !== currentLabel) {
                currentGroup = { dateLabel: label, entries: [] };
                groups.push(currentGroup);
                currentLabel = label;
            }
            currentGroup.entries.push(entry);
        });
        return groups;
    }

    getDateGroupLabel(date: Date): string {
        if (!date) {
            return 'Unknown date';
        }
        const entryDate = new Date(date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const entryDay = new Date(entryDate);
        entryDay.setHours(0, 0, 0, 0);

        if (entryDay.getTime() === today.getTime()) {
            return 'Today';
        }
        if (entryDay.getTime() === yesterday.getTime()) {
            return 'Yesterday';
        }
        return formatDisplayDate(entryDate);
    }

    getEventTypeClass(event: AuditTimelineEvent): string {
        if (event.sourceType === 'TASK') {
            return 'audit-event-task';
        }
        return 'audit-event-journal';
    }

    getSubmittedLabel(submitted: boolean): string {
        return submitted ? 'Submitted' : 'Not Submitted';
    }

    onScrollDown() {
        if (this.hasMoreToLoad && !this.isLoadingMore && this.isAuditLoaded) {
            this.isLoadingMore = true;
            this.currentOffset += this.pageSize;
            this.fetchTimelinePage(false);
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/entities/audit/auditdetails/audit-details.component.ts